/**
 * Portal onboarding + key-management calls against the developers-portal backend (`apps/api`). The
 * portal API is **single-env**: each env (staging01, production) runs its own deployment, so every call
 * targets a specific env's portal API via {@link portalApiBase} — onboarding provisions the partner in
 * each env with one call per env, and there is no server-side cross-env fan-out. Every call carries the
 * signed-in user's short-lived **access token** as the Bearer credential — the browser never holds the
 * partner api-key. The KYB business entity is then created against the payouts `/v2` API (also with the
 * access token), so onboarding is fully keyless.
 */
import type { ApiError, AuthContext } from './client';
import { createEntityWithAuth, listEntities } from './payouts';
import { getSonarSessionToken } from '@/lib/sonarSdk';
import { forceRefreshToken, handleSessionExpired } from '@/lib/authBridge';
import { GATE_ENV, portalApiBase, type EnvironmentId } from '@/config/environments';

/** Envs the backend provisions into. Same string values as the client's {@link EnvironmentId}. */
export type OnboardEnv = EnvironmentId;

/** The signed-in user's portal context — onboarding state + partner. */
export type PortalMe = {
  /** The token's own `sub` — used client-side only for analytics, never as an auth credential. */
  userId?: string;
  /** True when the user has a partner + business entity in this env. */
  onboarded: boolean;
  partnerName?: string;
  /** The company name the partner registered with, for display (dashboard top bar). */
  companyName?: string;
  /**
   * Whether this partner is cleared to operate (manage api-keys) in this env — an offline grant reported by
   * the deployment's `/me`. The live keys UI gates on the production `/me` value; the production `/keys`
   * routes also enforce it server-side (403 `PARTNER_NOT_OPERATIONAL`). Absent/false until the grant is given.
   */
  operational?: boolean;
  /** Other per-env partner config grants. */
  config?: {
    /** Whether this partner can create more than one business entity in this env. Absent/false until the grant is given. */
    allowMultipleBusinesses?: boolean;
  };
};

/** An error from a portal API call, carrying the backend's HTTP status + error code when present. */
export type PortalError = Error & { status?: number; code?: string };

async function portalJson<T>(env: EnvironmentId, path: string, accessToken: string, init: RequestInit = {}): Promise<T> {
  const send = (bearer: string) =>
    fetch(`${portalApiBase(env)}${path}`, { ...init, headers: { Authorization: `Bearer ${bearer}`, ...(init.headers ?? {}) } });

  let res = await send(accessToken);
  // On a 401 the access token is stale: force a single coordinated refresh and retry once. If the
  // refresh fails (refresh token expired), fall through to the error below and start the sign-in flow.
  if (res.status === 401) {
    const refreshed = await forceRefreshToken();
    if (refreshed) res = await send(refreshed);
    else handleSessionExpired();
  }

  const body = (await res.json().catch(() => undefined)) as (T & { error?: { message?: string; code?: string } }) | undefined;
  if (!res.ok) {
    const err = new Error(body?.error?.message || `Request failed (${res.status})`) as PortalError;
    err.status = res.status;
    err.code = body?.error?.code;
    throw err;
  }
  return (body ?? ({} as T)) as T;
}

/**
 * GET /me — the signed-in user's portal context (read from the env's melio-api DB: `sub → partner`
 * mapping + the partner's own-entity flag). The SPA's bootstrap/gate call, answered by the {@link
 * GATE_ENV} portal API by default; onboarding resume passes a specific env.
 */
export async function fetchMe(accessToken: string, signal?: AbortSignal, env: EnvironmentId = GATE_ENV): Promise<PortalMe> {
  return portalJson<PortalMe>(env, '/me', accessToken, { signal });
}

/**
 * The "apply for beta" application sent to `/onboard`. The server vets it (auto-approved for now),
 * persists it for offline review, and then provisions the partner. `email` is the business owner's,
 * collected on the wizard's owner step — the access token carries no email claim (auth-service puts it
 * only in the id_token), so it has to be sent explicitly.
 */
export type PartnerApplication = {
  /** The business owner's email, so a reviewer can reach the applicant. */
  email: string;
  companyName: string;
  website?: string;
  companyAddress?: string;
  industry?: string;
  /** 'own' (their own company) or 'platform' (a platform on behalf of others). */
  buildingFor: string;
  projectDescription: string;
};

/**
 * POST /onboard — submit the beta application; once it passes vetting (auto-approved for now) the server
 * provisions the partner and records the mapping. Responds with `{ ok: true }` — the partner name isn't
 * returned (read it from `/me`), and the api-key stays server-side. Rejects 409 (`PARTNER_ALREADY_EXISTS`)
 * if the user already has a partner, or 403 if the application isn't approved.
 */
export async function onboardPartner(
  env: EnvironmentId,
  accessToken: string,
  application: PartnerApplication,
  signal?: AbortSignal
): Promise<void> {
  await portalJson<{ ok: true }>(env, '/onboard', accessToken, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(application),
    signal,
  });
}

/**
 * Provisions the partner in one env, then reads its name back from that env's `/me`. Tolerant of a resume
 * (a resubmit after the entity create failed, or after a reload): when `/me` already names a partner here,
 * `/onboard` isn't called again, and should it 409 anyway (`PARTNER_ALREADY_EXISTS`, a race) we swallow it
 * — either way the partner exists, so `/me` yields its name for the (idempotent) KYB entity create that follows.
 */
export async function onboardOrExistingPartner(env: EnvironmentId, accessToken: string, application: PartnerApplication): Promise<string> {
  const existing = await fetchMe(accessToken, undefined, env);
  if (existing.partnerName) return existing.partnerName;
  try {
    await onboardPartner(env, accessToken, application);
  } catch (error) {
    const portalError = error as PortalError;
    if (portalError.code !== 'PARTNER_ALREADY_EXISTS' && portalError.status !== 409) throw error;
  }
  const me = await fetchMe(accessToken, undefined, env);
  if (!me.partnerName) throw new Error('Onboarding did not provision a partner in this environment.');
  return me.partnerName;
}

/**
 * Creates the partner's own business entity in one env, if it doesn't already have one
 * (check-before-create: `POST /v2/entities` uses a fresh `externalId` each call, so a blind retry on
 * resume would duplicate it). Both calls go through the shared access-token client (`authRequest`), so
 * they get the same 401 → refresh → retry-once handling as the dashboard — `getToken` is the app's
 * coordinated `getAccessToken`. The Sonar token is minted from the non-secret `partnerName` — no
 * api-key or publishable key ever enters the browser.
 */
export async function ensureEntityForEnv(
  env: EnvironmentId,
  getToken: AuthContext['getToken'],
  partnerName: string,
  entityBody: Record<string, unknown>
): Promise<void> {
  const actx: AuthContext = { env, getToken };
  const existing = await listEntities(actx);
  if (existing.length > 0) return;
  const sonarToken = await getSonarSessionToken(env, partnerName);
  try {
    await createEntityWithAuth(actx, entityBody, { sonarToken });
  } catch (error) {
    // An earlier attempt's create committed after the list above (it outran its gateway timeout, say):
    // the partner's one entity exists, complete, which is all this step is for.
    if ((error as ApiError).code !== 'DUPLICATE_ENTITY' || (await listEntities(actx)).length === 0) throw error;
  }
}

// ---- API-key management (dashboard) -----------------------------------------------------
/** Key metadata returned by the management API — never the secret value. */
export type ApiKeyMeta = {
  id: number;
  createdAt: string;
  /** When a draining key stops working, or when an expired one did. */
  expiredAt: string | null;
  /** `draining`: a previous key still working until `expiredAt`, after a rotation. */
  status: 'active' | 'draining' | 'expired';
  maskedKey: string | null;
};

/**
 * `GET /keys`'s body. `sonarKey` is the partner's non-secret `pk_<env>_…` publishable key — derived from
 * the partner name + env, not from any api-key, so it's one value beside the list — safe to hand to the
 * partner's own client-side code to initialize the Sonar SDK.
 */
export type KeyList = { keys: ApiKeyMeta[]; sonarKey: string };

/** GET /keys — the caller partner's key metadata for the env, plus its Sonar publishable key. */
export async function listKeys(accessToken: string, env: OnboardEnv): Promise<KeyList> {
  return portalJson<KeyList>(env, '/keys', accessToken);
}

/** What rotating returns: the new key (shown in full) and until when the previous one still works. */
export type RotatedKey = { apiKey: string; previousKeyUntil?: string };

/**
 * POST /keys/rotate — mint the new key. The previous one keeps working for 24h, or stops now with `immediate`
 * (for a leaked key).
 */
export async function rotateKey(accessToken: string, env: OnboardEnv, immediate: boolean): Promise<RotatedKey> {
  return portalJson<RotatedKey>(env, '/keys/rotate', accessToken, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ immediate }),
  });
}

// ---- Webhook endpoint (dashboard) --------------------------------------------------------
// The partner's single webhook endpoint, managed through this env's portal API (not payouts' `/v2/webhook`).
export type WebhookEventName =
  | 'api.entity.created'
  | 'api.entity.updated'
  | 'api.account.created'
  | 'api.account.updated'
  | 'api.account.deleted'
  | 'api.payment.created'
  | 'api.payment.updated'
  | 'api.limitation.updated';

/** The endpoint. Its deliveries are signed with the partner's webhook signing secret (see {@link SigningSecret}). */
export type Webhook = {
  url: string;
  events: WebhookEventName[];
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
  /** The signing secret's metadata (never the secret), or null when the endpoint has none — rotate creates one. */
  signingSecret?: SigningSecretInfo | null;
};

/** `PATCH /webhook`'s body: creates the endpoint (`url` + `events` required) or updates only the fields sent. */
export type PatchWebhookBody = {
  url?: string;
  events?: WebhookEventName[];
  isActive?: boolean;
};

/** GET /webhook — the endpoint, or null when none is configured (404). */
export async function getWebhook(accessToken: string, env: OnboardEnv, signal?: AbortSignal): Promise<Webhook | null> {
  try {
    return await portalJson<Webhook>(env, '/webhook', accessToken, { signal });
  } catch (error) {
    if ((error as PortalError).status === 404) return null;
    throw error;
  }
}

/**
 * PATCH /webhook — create or update the endpoint. Creating it also creates its signing secret, which only that
 * response carries (as `signingSecret.secret`): it can't be read back, so show it right away.
 */
export async function patchWebhook(
  accessToken: string,
  env: OnboardEnv,
  body: PatchWebhookBody
): Promise<Webhook & { signingSecret?: SigningSecretInfo | SigningSecret | null }> {
  return portalJson<Webhook & { signingSecret?: SigningSecretInfo | SigningSecret | null }>(env, '/webhook', accessToken, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

/** DELETE /webhook — remove the endpoint. Its signing secret stops working; a new endpoint gets a new one. */
export async function deleteWebhook(accessToken: string, env: OnboardEnv): Promise<void> {
  await portalJson(env, '/webhook', accessToken, { method: 'DELETE' });
}

/** GET /webhook/events — event types an endpoint can subscribe to. */
export async function listWebhookEvents(accessToken: string, env: OnboardEnv, signal?: AbortSignal): Promise<WebhookEventName[]> {
  const { events } = await portalJson<{ events: WebhookEventName[] }>(env, '/webhook/events', accessToken, { signal });
  return events;
}

/**
 * The signing secret's metadata, which {@link getWebhook} returns. While a rotation's overlap lasts,
 * `previousSigningUntil` says until when the previous secret also signs. The masked secrets are the list preview,
 * like an API key's (`whsec_••••` and the last 4 characters); `null` when the secret couldn't be read.
 */
export type SigningSecretInfo = {
  createdAt: string;
  maskedSecret?: string | null;
  previousSigningUntil?: string;
  previousMaskedSecret?: string | null;
};

/**
 * The webhook signing secret (`whsec_<base64>`, Standard Webhooks) deliveries are signed with. It can't call the API. It is
 * returned only when created (endpoint create, rotate) and can't be read back; a lost one is replaced by rotating.
 */
export type SigningSecret = SigningSecretInfo & { secret: string };

/**
 * POST /webhook/signing-secret/rotate — a new signing secret, returned once. The previous one keeps signing for 24h,
 * or stops right away with `immediate` (for a leaked secret).
 */
export async function rotateSigningSecret(accessToken: string, env: OnboardEnv, immediate: boolean): Promise<SigningSecret> {
  return portalJson<SigningSecret>(env, '/webhook/signing-secret/rotate', accessToken, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ immediate }),
  });
}
